import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
const MotionBox = motion(Box);
const TICKET_CLIP = 'polygon(0 0, calc(100% - 11px) 0, 100% 11px, 100% 100%, 11px 100%, 0 calc(100% - 11px))';
type SealTone = 'whatsapp' | 'phone' | 'gold';
const tones: Record<SealTone, {
  border: string;
  background: string;
  color: string;
  glow: string;
  iconBg: string;
}> = {
  whatsapp: {
    border: '#25d366',
    background: 'radial-gradient(120% 120% at 50% 22%, #56e58c 0%, #25d366 45%, #128c42 100%)',
    color: '#07220f',
    glow: 'rgba(37,211,102,0.45)',
    iconBg: 'rgba(7,34,15,0.18)'
  },
  phone: {
    border: '#c41230',
    background: 'radial-gradient(120% 120% at 50% 22%, #e84664 0%, #c41230 45%, #78091c 100%)',
    color: '#fff6f0',
    glow: 'rgba(196,18,48,0.45)',
    iconBg: 'rgba(0,0,0,0.22)'
  },
  gold: {
    border: '#f2b705',
    background: 'radial-gradient(120% 120% at 50% 22%, #ffd968 0%, #f2b705 45%, #a8750a 100%)',
    color: '#2a1a00',
    glow: 'rgba(242,183,5,0.45)',
    iconBg: 'rgba(42,26,0,0.16)'
  }
};
interface SealButtonProps {
  href: string;
  tone: SealTone;
  label: string;
  caption?: string;
  icon: React.ReactNode;
  external?: boolean;
  ariaLabel?: string;
  fullWidth?: boolean;
  size?: 'md' | 'lg';
}
export function SealButton({
  href,
  tone,
  label,
  caption,
  icon,
  external = false,
  ariaLabel,
  fullWidth = false,
  size = 'lg'
}: SealButtonProps) {
  const palette = tones[tone];
  const isLarge = size === 'lg';
  return <MotionBox as="a" href={href} aria-label={ariaLabel ?? label} {...external ? {
    target: '_blank',
    rel: 'noopener noreferrer'
  } : {}} display="inline-block" w={fullWidth ? '100%' : 'auto'} p="3px" border="1px solid" borderColor="rgba(242,183,5,0.85)" bg="rgba(13,13,13,0.55)" sx={{
    clipPath: TICKET_CLIP
  }} style={{
    transformOrigin: 'center'
  }} initial={false} whileHover={{
    rotate: 1.5,
    y: -3,
    boxShadow: `0 0 0 1px rgba(242,183,5,0.5), 0 0 22px rgba(242,183,5,0.55), 0 18px 34px ${palette.glow}`
  }} whileFocus={{
    rotate: 1.5,
    y: -3,
    boxShadow: `0 0 0 1px rgba(242,183,5,0.5), 0 0 22px rgba(242,183,5,0.55), 0 18px 34px ${palette.glow}`
  }} whileTap={{
    scale: 0.97,
    rotate: 0,
    y: 0
  }} transition={{
    type: 'spring',
    stiffness: 420,
    damping: 16,
    mass: 0.6
  }} data-id="element-141" data-mp="nj5amxj6yg4e">
      <Flex direction="column" align="center" justify="center" gap={0.5} px={isLarge ? {
      base: 3.5,
      md: 4
    } : 3} py={isLarge ? 2 : 1.5} border="2px solid" borderColor={palette.border} bg={palette.background} color={palette.color} sx={{
      clipPath: TICKET_CLIP
    }} data-id="element-142" data-mp="u0gihw0yc56q">
        <Flex align="center" justify="center" w={isLarge ? '1.45rem' : '1.35rem'} h={isLarge ? '1.45rem' : '1.35rem'} borderRadius="full" border="1px solid" borderColor="rgba(242,183,5,0.9)" bg={palette.iconBg} flexShrink={0} data-id="element-143" data-mp="30pv2yw14zu9i">
          {icon}
        </Flex>

        <Text as="span" fontFamily="heading" fontWeight={700} textTransform="uppercase" letterSpacing="0.16em" lineHeight={1.15} textAlign="center" fontSize={isLarge ? '0.66rem' : '0.62rem'} data-id="element-144" data-mp="377oiroex9lzl">
          {label}
        </Text>

        {caption && <Text as="span" fontFamily="body" fontSize="0.56rem" fontWeight={600} letterSpacing="0.08em" opacity={0.85} data-id="element-145" data-mp="29lhe6eimbtn8">
            {caption}
          </Text>}
      </Flex>
    </MotionBox>;
}